import { memo, useMemo, type CSSProperties } from "react";
import { Area, AreaChart, CartesianGrid, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useUserPnlHistory, useUserTvlHistory } from "@/hooks/infinifi/useUserHistory";
import { usePortfolioStore } from "@/zustand/PortfolioStore";
import { useUserTvl } from "@/hooks/infinifi/useUserTvl";

type ChartPoint = {
    time: number;
    tvl?: number;
    pnl?: number;
};

const Y_AXIS_TICK_COUNT = 5;

const isFiniteNumber = (value: number | undefined): value is number =>
    typeof value === "number" && Number.isFinite(value);

const buildIntegerTicks = (values: Array<number | undefined>, tickCount = Y_AXIS_TICK_COUNT): number[] => {
    const numericValues = values.filter(isFiniteNumber);

    if (numericValues.length === 0) {
        return Array.from({ length: tickCount }, (_, index) => index);
    }

    let minValue = numericValues[0];
    let maxValue = numericValues[0];

    numericValues.forEach((value) => {
        if (value < minValue) minValue = value;
        if (value > maxValue) maxValue = value;
    });

    const minTick = Math.floor(minValue);
    const maxTick = Math.ceil(maxValue);

    if (minTick === maxTick) {
        return Array.from({ length: tickCount }, (_, index) => minTick + index);
    }

    const step = Math.max(1, Math.ceil((maxTick - minTick) / (tickCount - 1)));
    return Array.from({ length: tickCount }, (_, index) => minTick + index * step);
};

const formatDate = (value: number) =>
    new Date(value).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });

const formatValue = (value: number) =>
    `$${value.toLocaleString("en-US", {
        maximumFractionDigits: 0,
    })}`;

const graphStyles = {
    container: {
        fontFamily: "var(--font-base)",
        background: "var(--portfolio-graph-bg)",
    },
    loading: {
        background: "var(--portfolio-graph-loading-bg)",
    },
    legend: {
        display: "flex",
        justifyContent: "center",
        gap: "16px",
        paddingTop: "8px",
    },
    legendItem: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
    legendText: {
        fontSize: "12px",
        color: "var(--portfolio-graph-legend-text)",
    },
    pnlGradientStart: {
        stopColor: "var(--portfolio-graph-pnl-stroke)",
    },
    pnlGradientEnd: {
        stopColor: "var(--portfolio-graph-pnl-stroke)",
    },
    xAxisTick: {
        fontSize: 12,
        fill: "var(--portfolio-graph-x-axis-tick)",
    },
    tvlTick: {
        fontSize: 12,
        fill: "var(--portfolio-graph-tvl-tick)",
    },
    pnlTick: {
        fontSize: 12,
        fill: "var(--portfolio-graph-pnl-tick)",
    },
    tooltip: {
        background: "var(--portfolio-graph-tooltip-bg)",
        borderColor: "var(--portfolio-graph-tooltip-border)",
        color: "var(--portfolio-graph-tooltip-text)",
    },
} satisfies Record<string, CSSProperties>;

const CustomLegend = (props: any) => {
    const { payload } = props;
    if (!payload) return null;

    // Explicitly order: TVL first, then PnL
    const orderedPayload = [...payload].sort((a, b) => {
        if (a.value === "TVL") return -1;
        if (b.value === "TVL") return 1;
        return 0;
    });

    return (
        <div style={graphStyles.legend}>
            {orderedPayload.map((entry: any, index: number) => (
                <div key={`legend-${index}`} style={graphStyles.legendItem}>
                    <svg width="8" height="8">
                        <circle cx="4" cy="4" r="4" fill={entry.color} />
                    </svg>
                    <span style={graphStyles.legendText}>{entry.value}</span>
                </div>
            ))}
        </div>
    );
};

export const PortfolioGraph = memo(function PortfolioGraph() {
    const { graphInterval } = usePortfolioStore();
    const { userTotalTvl, userTotalRewards, isLoading: isUserTvlLoading } = useUserTvl();
    const { data: tvlHistory, isLoading: isTvlLoading } = useUserTvlHistory(graphInterval);
    const { data: pnlHistory, isLoading: isPnlLoading } = useUserPnlHistory(graphInterval);

    const chartData = useMemo<ChartPoint[]>(() => {
        const points = new Map<number, ChartPoint>();

        tvlHistory?.dataPoints.forEach((point) => {
            points.set(point.time, { time: point.time, tvl: point.value });
        });

        pnlHistory?.dataPoints.forEach((point) => {
            const existing = points.get(point.time);
            points.set(point.time, { time: point.time, tvl: existing?.tvl, pnl: point.value });
        });

        const final = Array.from(points.values()).sort((a, b) => a.time - b.time);

        // append current point, use userTotalTvl and userTotalRewards from useUserTvl for the most up-to-date values
        const now = Date.now();
        if (!points.has(now)) {
            final.push({ time: now, tvl: userTotalTvl, pnl: userTotalRewards });
        }

        return final;
    }, [tvlHistory, pnlHistory, userTotalTvl, userTotalRewards]);

    const { tvlTicks, pnlTicks } = useMemo(
        () => ({
            tvlTicks: buildIntegerTicks(chartData.map((point) => point.tvl)),
            pnlTicks: buildIntegerTicks(chartData.map((point) => point.pnl)),
        }),
        [chartData],
    );

    const isLoading = isTvlLoading || isPnlLoading || isUserTvlLoading;

    return (
        <div className="flex flex-col gap-4 pt-4">
            <div
                className="w-full h-[400px] rounded-md [&_.recharts-surface:focus]:outline-none [&_.recharts-surface:focus-visible]:outline-none [&_.recharts-wrapper:focus]:outline-none [&_.recharts-wrapper:focus-visible]:outline-none"
                style={graphStyles.container}
            >
                {isLoading ? (
                    <div className="h-full w-full animate-pulse rounded-md" style={graphStyles.loading} />
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 24, bottom: 8 }}>
                            <defs>
                                <linearGradient id="pnlGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" style={graphStyles.pnlGradientStart} stopOpacity={0.15} />
                                    <stop offset="100%" style={graphStyles.pnlGradientEnd} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="5 5"
                                stroke="var(--portfolio-graph-grid)"
                                syncWithTicks
                                repeatCount={5}
                            />
                            <XAxis
                                dataKey="time"
                                tickFormatter={formatDate}
                                type="number"
                                domain={["dataMin", "dataMax"]}
                                tick={graphStyles.xAxisTick}
                                tickCount={30}
                            />
                            <YAxis
                                yAxisId="Total portfolio value"
                                orientation="left"
                                tickFormatter={formatValue}
                                ticks={tvlTicks}
                                domain={[tvlTicks[0], tvlTicks[tvlTicks.length - 1]]}
                                allowDecimals={false}
                                tick={graphStyles.tvlTick}
                                width={64}
                            />
                            <YAxis
                                yAxisId="Interest earned"
                                orientation="right"
                                tickFormatter={formatValue}
                                ticks={pnlTicks}
                                domain={[pnlTicks[0], pnlTicks[pnlTicks.length - 1]]}
                                allowDecimals={false}
                                tick={graphStyles.pnlTick}
                                width={64}
                            />
                            <Tooltip
                                labelFormatter={(value) => formatDate(Number(value))}
                                formatter={(value: number, name: string) => [formatValue(value), name]}
                                separator=": "
                                contentStyle={graphStyles.tooltip}
                                isAnimationActive={false}
                                animationDuration={0}
                                wrapperStyle={{ transition: "none" }}
                            />
                            <Line
                                yAxisId="Total portfolio value"
                                type="monotone"
                                dataKey="tvl"
                                name="Total portfolio value"
                                stroke="var(--portfolio-graph-tvl-stroke)"
                                strokeWidth={2}
                                // fill="rgb(26 114 113)"
                                dot={false}
                                isAnimationActive={!isLoading}
                                animationDuration={500}
                                zIndex={1}
                            />
                            <Area
                                yAxisId="Interest earned"
                                type="monotone"
                                dataKey="pnl"
                                name="Interest earned"
                                stroke="var(--portfolio-graph-pnl-stroke)"
                                strokeWidth={2}
                                fill="url(#pnlGradient)"
                                dot={false}
                                isAnimationActive={!isLoading}
                                animationDuration={500}
                                zIndex={0}
                            />
                            <Legend content={CustomLegend} />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
});

PortfolioGraph.displayName = "PortfolioGraph";
